import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import { TypeCargoUser } from '@/lib/enums'
import { useCargo, useCountFilters, useResetFilters, useSearch, useTypeUser } from '../state/filters'
import { Button } from '@/components/ui/button'
import Icon from '@/components/ui/icon'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { useToast } from '@/lib/hooks/use-toast'
import { useState } from 'react'
import { formatNome } from '@/utils/format'

enum TypeSelectCargoAdmin {
  TODOS = 'Todos',
  ANALISTA = 'ANALISTA',
  FINANCEIRO = 'FINANCEIRO',
  JURIDICO = 'JURIDICO'
}

const combinedCargos = { ...TypeSelectCargoAdmin, ...TypeCargoUser }

export default function ListaUserAllFilter() {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useSearch()
  const resetFilters = useResetFilters()
  const countFilters = useCountFilters()

  return (
    <div className="flex flex-1 flex-col gap-4 md:flex-row">
      <Input
        placeholder="Buscar por CPF, Nome ou Email..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        className="w-full md:w-1/3 bg-input"
      />

      {/* Radix passa o estado correto, então use diretamente setOpen */}
      <Dialog open={open} onOpenChange={setOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>
              <Button variant="outline" className="relative flex w-full items-center gap-2 md:w-auto">
                <Icon name="PlusCircle" />
                Filtros
                {countFilters > 0 && (
                  <Badge variant="destructive" className="absolute -bottom-3 -right-2 px-1.5 py-0.5">
                    {countFilters}
                  </Badge>
                )}
              </Button>
            </DialogTrigger>
          </TooltipTrigger>
          <TooltipContent>Todos os filtros aqui</TooltipContent>
        </Tooltip>

        <DialogContent className="max-w-screen-sm">
          <DialogHeader>
            <DialogTitle>Filtre a lista de usuários</DialogTitle>
            <DialogDescription>
              <Button
                variant="link"
                className="px-0"
                onClick={() => {
                  resetFilters()
                  setOpen(false)
                }}
              >
                Limpar filtros
              </Button>
            </DialogDescription>
          </DialogHeader>

          <FiltersForm onFinish={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  )
}

const FormSchema = z.object({
  cargo: z.string().optional(),
  typeUser: z.string().optional()
})

interface FilterFormProps {
  onFinish: () => void
}

function FiltersForm({ onFinish }: FilterFormProps) {
  const { toast } = useToast()
  const [selectedCargo, setSelectedCargo] = useCargo()
  const [selectedTypeUser, setSelectedTypeUser] = useTypeUser()

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      cargo: selectedCargo || '',
      typeUser: selectedTypeUser || ''
    }
  })

  function onSubmit(data: z.infer<typeof FormSchema>) {
    if (data.cargo !== undefined) setSelectedCargo(data.cargo)
    if (data.typeUser !== undefined) setSelectedTypeUser(data.typeUser)

    toast({ title: 'Filtros aplicados' })
    onFinish?.()
  }

  return (
    <div className="w-full">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="cargo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cargo do Usuário</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Filtrar por cargo" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.values(combinedCargos).map((cargo, idx) => (
                      <SelectItem key={idx} value={cargo}>
                        {formatNome(cargo)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>Filtre pelos cargos dos usuários.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="typeUser"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status do Usuário</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Filtrar por status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {['Todos', 'ADMIN', 'USUARIO'].map(status => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>Filtre pelo status do usuário.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit">Aplicar filtros</Button>
        </form>
      </Form>
    </div>
  )
}
